import { useState, useEffect, useRef } from "react";

// ─── DESIGN TOKENS (V1 DNA) ───
const GOLD = "#C9A84C";
const DARK = "#0A0A0A";
const DARK_CARD = "#111111";
const DARK_DEEPER = "#060606";
const DARK_BORDER = "#1A1A1A";
const DARK_BORDER_HOVER = "#2A2520";
const TEXT_PRIMARY = "#F5F0E8";
const TEXT_HIGH = "#D5CFC4";
const TEXT_SECONDARY = "#8A8578";
const TEXT_DIM = "#5A5549";

const PAGES = ["Home", "About", "Services", "Testimonials"];
const NAV_H = 72;

// ─── HOOKS ───
function useInView(th = 0.12) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold: th });
    o.observe(el);
    return () => o.disconnect();
  }, [th]);
  return [ref, v];
}

function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const h = () => setY(window.scrollY);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return y;
}

// ─── ANIMATION COMPONENTS ───
function Reveal({ children, delay = 0, direction = "up", style = {} }) {
  const [ref, v] = useInView();
  const t = { up: "translateY(50px)", left: "translateX(50px)", right: "translateX(-50px)", none: "none" };
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0, transform: v ? "translate(0,0)" : (t[direction] || t.up),
      transition: `opacity 0.85s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.85s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>{children}</div>
  );
}

// Staggered text reveal for headlines
function StaggerText({ text, delay = 0, style = {} }) {
  const [ref, v] = useInView();
  const words = text.split(" ");
  return (
    <span ref={ref} style={style}>
      {words.map((word, i) => (
        <span key={i} style={{
          display: "inline-block",
          opacity: v ? 1 : 0,
          transform: v ? "translateY(0)" : "translateY(14px)",
          transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay + i * 0.04}s, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay + i * 0.04}s`,
        }}>{word}&nbsp;</span>
      ))}
    </span>
  );
}

// Parallax
function Parallax({ children, speed = 0.05, style = {} }) {
  const ref = useRef(null);
  const [off, setOff] = useState(0);
  useEffect(() => {
    const h = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setOff((window.innerHeight / 2 - rect.top - rect.height / 2) * speed);
      }
    };
    window.addEventListener("scroll", h, { passive: true });
    h();
    return () => window.removeEventListener("scroll", h);
  }, [speed]);
  return <div ref={ref} style={{ transform: `translateY(${off}px)`, ...style }}>{children}</div>;
}

// Animated number
function AnimNum({ target, prefix = "", suffix = "", formatK = false }) {
  const [ref, v] = useInView(0.3);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!v) return;
    const num = parseInt(target.toString().replace(/\D/g, ""));
    const s = performance.now();
    const step = (now) => {
      const p = Math.min((now - s) / 1600, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 4)) * num));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [v, target]);
  let display = val;
  if (formatK) {
    if (val >= 1000) display = (val / 1000).toFixed(0) + "M";
    else if (val > 0) display = val + "K";
    else display = "0";
  }
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

// Drawing line
function DrawLine({ width = "60px", color = GOLD }) {
  const [ref, v] = useInView();
  return (
    <div ref={ref} style={{
      width: v ? width : "0px", height: "1px", background: color,
      transition: "width 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s",
      marginBottom: "24px",
    }} />
  );
}

// Particle field
function ParticleField() {
  const canvasRef = useRef(null);
  const anim = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w = c.width = c.offsetWidth, h = c.height = c.offsetHeight;
    const pts = Array.from({ length: Math.floor((w * h) / 15000) }, () => ({
      x: Math.random() * w, y: Math.random() * h, r: Math.random() * 1.2 + 0.3,
      dx: (Math.random() - 0.5) * 0.25, dy: (Math.random() - 0.5) * 0.12 - 0.08,
      o: Math.random() * 0.4 + 0.08,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
        if (d < 100) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(201,168,76,${0.05 * (1 - d / 100)})`; ctx.lineWidth = 0.5; ctx.stroke(); }
      }
      anim.current = requestAnimationFrame(draw);
    };
    draw();
    const rs = () => { w = c.width = c.offsetWidth; h = c.height = c.offsetHeight; };
    window.addEventListener("resize", rs);
    return () => { cancelAnimationFrame(anim.current); window.removeEventListener("resize", rs); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

function Grain() {
  return <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, opacity: 0.025, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />;
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const h = () => { const t = document.documentElement.scrollHeight - window.innerHeight; setP(t > 0 ? window.scrollY / t : 0); };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return <div style={{ position: "fixed", top: 0, left: 0, width: `${p * 100}%`, height: "2px", background: `linear-gradient(90deg, ${GOLD}80, ${GOLD})`, zIndex: 10001, transition: "width 0.05s linear" }} />;
}

function Tag({ text }) {
  return <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
    <span style={{ width: "28px", height: "1px", background: GOLD }} />
    <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "12px", letterSpacing: "4px", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>{text}</span>
  </div>;
}

// Page transition
function PageTransition({ children, pageKey }) {
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(false); const t = requestAnimationFrame(() => requestAnimationFrame(() => setShow(true))); return () => cancelAnimationFrame(t); }, [pageKey]);
  return <div style={{ opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease" }}>{children}</div>;
}

// ─── SVG ICONS ───
function LinkedInIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>; }
function TelegramIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>; }
function EmailIcon({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4L12 13 2 4"/></svg>; }

// Logo as embedded SVG approximation
function Logo({ size = 42 }) {
  return (
    <img
      src={process.env.PUBLIC_URL + "/logo.png"}
      alt="CryptoBoostra"
      style={{
        width: size, height: size, borderRadius: "4px",
        objectFit: "cover", flexShrink: 0,
        background: DARK,
      }}
    />
  );
}

// ─── DATA ───
const SERVICES = [
  { num: "01", title: "Token Launch Structuring", sub: "Liquidity, Float, and Unlock Alignment", desc: "We don't just plan your TGE. We stress-test your entire token structure: liquidity depth, unlock schedules, float management, and exchange timing. Most launches fail in the first 72 hours from things that could have been caught early.", tags: ["Tokenomics Audit", "TGE Execution", "Liquidity Planning", "Unlock Design"] },
  { num: "02", title: "Exchange and Market Maker Strategy", sub: "Execution, Not Just Introductions", desc: "We don't hand you a list of exchanges and walk away. We negotiate listing terms, assess market makers, and help structure the right setup for launch. Direct relationships with Tier 1 to 3 exchanges, no unnecessary chains inflating your costs.", tags: ["Exchange Listings", "Market Maker Selection", "Cost Negotiation"] },
  { num: "03", title: "Private OTC Capital", sub: "OTC, Strategic Investors, and VCs", desc: "We connect you with the right counterparties for private OTC opportunities and help structure the deal before an introduction is made. We evaluate opportunities based on liquidity depth and exit viability, not hype.", tags: ["Private Allocations", "OTC Deals", "Deal Structuring", "Due Diligence"] },
  { num: "04", title: "Post-TGE Support", sub: "Token Market Structure and Post-Listing Support", desc: "Support for the market after launch, including liquidity, exchange coordination, and ongoing market structure.", tags: ["Liquidity Support", "Exchange Coordination", "Market Structure", "Unlock Monitoring"] },
];

const STATS = [
  { num: 1000, prefix: "$", suffix: "+", label: "In Executed Deals", formatK: true },
  { num: 100, prefix: "", suffix: "+", label: "Projects Advised" },
  { num: 200, prefix: "", suffix: "+", label: "Exchange and VC Partners" },
  { num: 4, prefix: "", suffix: "+", label: "Years Operating" },
];

const TESTIMONIALS = [
  { quote: "As an investor, I trust their deal flow because they only bring me projects they have personally vetted. That kind of filter is rare and valuable in this market.", name: "Deborah Peters", role: "Angel Investor" },
  { quote: "CryptoBoostra connected us with investors who actually followed through. They prepared our deal terms, made the introductions, and stayed involved until the capital was in. Most people promise connections. These guys delivered results.", name: "Ayhan Karakaya", role: "Founder, Bitchemical LAB and AI Technologies" },
  { quote: "CryptoBoostra restructured our entire listing strategy. They helped us pick the right exchanges for our stage, timed the listings properly, and protected our treasury from a deal that would have cost us three times more. That level of planning made all the difference.", name: "Alex R.", role: "CEO, Arken Labs" },
  { quote: "We came to CryptoBoostra three weeks before TGE with a liquidity problem we didn't even know we had. They restructured our unlock schedule, renegotiated our market maker terms, and we launched without a single surprise. That kind of attention to structure is hard to find.", name: "K. Chen", role: "CTO, Verdo Network" },
];

const FAILURES = [
  { stat: "73%", text: "of token launches lose significant value within 30 days. Usually from poor structure, not just weak demand." },
  { stat: "40%+", text: "of founders overpay for exchange listings because they don't reach the right decision makers directly." },
  { stat: "1 in 3", text: "market maker arrangements contain terms founders should look at more closely." },
];

const OUTCOMES = [
  "You know what your liquidity setup needs to look like before going live.",
  "You go into market maker agreements knowing exactly what you're signing",
  "Your unlock schedule is structured to support your chart, not crush it",
  "You enter listings with a clear exchange strategy",
  "Your capital deals come with terms that make sense for the project",
];

const TEAM = [
  { name: "Merit Nkwocha", role: "Founder, Deals and Strategy", links: [
    { type: "linkedin", url: "https://www.linkedin.com/in/meritnkwocha" },
    { type: "telegram", url: "https://t.me/merit_nk" },
    { type: "email", url: "mailto:merit@cryptoboostra.com" },
  ]},
  { name: "John Edwin", role: "Operations", links: [
    { type: "email", url: "mailto:johnedwin200@gmail.com" },
    { type: "telegram", url: "https://t.me/Eddycryptoboostra" },
  ]},
  { name: "Franklin Franklin", role: "Partnerships", links: [
    { type: "telegram", url: "https://t.me/Franklincryptoboostra" },
  ]},
  { name: "Constance Carpenter", role: "Client Relations", links: [
    { type: "telegram", url: "https://t.me/Constance_cryptoboostra" },
  ]},
];

const FAQS = [
  { q: "How does CryptoBoostra make money?", a: "We work on a combination of retainers and success-based fees, depending on the engagement. We are focused on outcomes, not just activity. We do well when you do well." },
  { q: "What types of projects do you work with?", a: "We work with L1s, L2s, DeFi protocols, and serious early-stage teams preparing for token launch. If you are thinking about token structure, exchange strategy, or private capital, you are in our lane. If you are looking for a quick listing without getting the foundations right, we are probably not the right fit." },
  { q: "How long does a typical engagement last?", a: "It depends on the scope. A focused engagement like exchange strategy might take a few weeks. Full token launch structuring and execution can run several months. We stay involved as long as there are critical decisions on the table, not just until introductions are made." },
  { q: "How is CryptoBoostra different?", a: "Most agencies make introductions and step back. We stay in the deal room, negotiating terms, stress-testing structure, and working through the details that matter. Our focus is on liquidity, market structure, deal terms, and execution. Not vanity metrics." },
  { q: "Do you work with all projects?", a: "No. We work with teams that are serious about getting the structure right before going live. If that is not the priority, we are not the right partner." },
  { q: "What does getting started look like?", a: "It starts with a conversation. We learn where you are, what you're working on, and what you need. From there, we map out a plan and move when you're ready." },
];

// ─── SERVICE CARD WITH SHIMMER ───
function ServiceCard({ service }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      background: DARK_CARD, border: `1px solid ${hov ? DARK_BORDER_HOVER : DARK_BORDER}`,
      padding: "44px 32px", transition: "all 0.4s ease",
      transform: hov ? "translateY(-5px)" : "none",
      position: "relative", overflow: "hidden", cursor: "default",
    }}>
      {/* Gold shimmer on hover */}
      <div style={{
        position: "absolute", top: 0, left: hov ? "120%" : "-30%",
        width: "20%", height: "100%",
        background: `linear-gradient(90deg, transparent, ${GOLD}12, transparent)`,
        transition: "left 0.6s ease", pointerEvents: "none",
        transform: "skewX(-15deg)",
      }} />
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "2px",
        background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
        opacity: hov ? 1 : 0, transition: "opacity 0.4s ease",
      }} />
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "13px", fontWeight: 400, color: GOLD, letterSpacing: "2px", marginBottom: "16px", position: "relative" }}>{service.num}</div>
      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "24px", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 4px", position: "relative" }}>{service.title}</h3>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM, marginBottom: "16px", position: "relative" }}>{service.sub}</div>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "13px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: "0 0 24px", position: "relative" }}>{service.desc}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", position: "relative" }}>
        {service.tags.map((tag, i) => (
          <span key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "9px", fontWeight: 500, letterSpacing: "1.5px", textTransform: "uppercase", padding: "4px 10px", background: DARK_DEEPER, color: TEXT_DIM, border: `1px solid ${DARK_BORDER}` }}>{tag}</span>
        ))}
      </div>
    </div>
  );
}

// Service row
function ServiceRow({ service, index }) {
  const [hov, setHov] = useState(false);
  return (
    <Reveal delay={index * 0.07}>
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
        display: "grid", gridTemplateColumns: "50px 1fr auto",
        gap: "28px", alignItems: "center", padding: "28px 20px",
        background: hov ? DARK_CARD : "transparent",
        borderBottom: `1px solid ${hov ? DARK_BORDER_HOVER : DARK_BORDER}`,
        transition: "all 0.35s ease", cursor: "default",
      }}>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "14px", fontWeight: 400, color: hov ? GOLD : TEXT_DIM, transition: "color 0.3s ease" }}>{service.num}</span>
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(18px, 2vw, 24px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 2px" }}>{service.title}</h3>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: hov ? TEXT_SECONDARY : TEXT_DIM, transition: "color 0.3s ease" }}>{service.sub}</div>
          <div style={{ maxHeight: hov ? "120px" : "0", overflow: "hidden", transition: "max-height 0.5s ease, opacity 0.4s ease, margin 0.4s ease", opacity: hov ? 1 : 0, marginTop: hov ? "12px" : "0" }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "13px", color: TEXT_SECONDARY, lineHeight: 1.65, margin: 0, maxWidth: "540px" }}>{service.desc}</p>
          </div>
        </div>
        <div style={{ width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${hov ? GOLD + "40" : DARK_BORDER}`, color: hov ? GOLD : TEXT_DIM, fontSize: "16px", transition: "all 0.3s ease", transform: hov ? "rotate(45deg)" : "rotate(0deg)" }}>↗</div>
      </div>
    </Reveal>
  );
}

// Card hover
function CardHover({ children }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      background: DARK_CARD, border: `1px solid ${hov ? DARK_BORDER_HOVER : DARK_BORDER}`,
      padding: "40px 32px", transition: "all 0.35s ease", cursor: "default",
      transform: hov ? "translateY(-4px)" : "none",
    }}>{children}</div>
  );
}

// Testimonial carousel with quote marks
function TestimonialCarousel() {
  const [active, setActive] = useState(0);
  useEffect(() => { const t = setInterval(() => setActive(p => (p + 1) % TESTIMONIALS.length), 10000); return () => clearInterval(t); }, []);
  const t = TESTIMONIALS[active];

  return (
    <section id="testimonials" style={{
      padding: "clamp(80px, 10vw, 130px) 5vw", scrollMarginTop: `${NAV_H + 20}px`,
      borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`, background: DARK_DEEPER,
    }}>
      <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
        <Reveal>
          <Tag text="Testimonials" />
          <h2 style={{ ...h2Style, marginBottom: "56px" }}>
            <StaggerText text="Trusted by" />{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Founders and Investors" delay={0.3} /></span>
          </h2>
        </Reveal>

        <div style={{ position: "relative", minHeight: "260px" }}>
          {/* Large quote marks */}
          <div key={`q-${active}`} style={{
            position: "absolute", top: "-20px", left: "50%", transform: "translateX(-50%)",
            fontFamily: "'Cormorant Garamond', serif", fontSize: "160px", fontWeight: 300,
            color: GOLD + "08", lineHeight: 1, pointerEvents: "none",
            animation: "fadeUp 0.8s ease forwards",
          }}>"</div>

          <div key={active} style={{ animation: "fadeUp 0.6s ease forwards", position: "relative", zIndex: 1 }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(20px, 2.5vw, 28px)", fontWeight: 500, fontStyle: "italic",
              color: TEXT_PRIMARY, lineHeight: 1.65, margin: "0 auto 32px", maxWidth: "660px",
            }}>"{t.quote}"</p>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", fontWeight: 500, color: TEXT_PRIMARY, marginBottom: "4px" }}>{t.name}</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM }}>{t.role}</div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "36px" }}>
          {TESTIMONIALS.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} style={{
              width: active === i ? "28px" : "8px", height: "8px", borderRadius: "4px",
              border: "none", cursor: "pointer", background: active === i ? GOLD : DARK_BORDER,
              transition: "all 0.4s ease", padding: 0,
            }} />
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA
function CTABlock({ navigate }) {
  return (
    <section style={{ padding: "clamp(80px, 10vw, 120px) 5vw", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at center, ${GOLD}06 0%, transparent 60%)`, pointerEvents: "none" }} />
      <Reveal>
        <Tag text="Next Step" />
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(30px, 5vw, 56px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 16px", lineHeight: 1.15 }}>
          <StaggerText text="Ready to" />{" "}
          <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Get This Right" delay={0.15} /></span>
          <span style={{ color: GOLD }}>?</span>
        </h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: TEXT_SECONDARY, margin: "0 auto 40px", maxWidth: "480px", lineHeight: 1.7 }}>
          Launching a token, or exploring private OTC capital?, let's look at where you are and figure out what comes next.
        </p>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => navigate("Contact")} style={btnPrimary}>Let's Assess Your Structure →</button>
          <button onClick={() => navigate("Services")} style={btnOutline}>View Services</button>
        </div>
      </Reveal>
    </section>
  );
}

// FAQ accordion - only one open at a time
function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="faq" style={{ padding: "clamp(60px, 8vw, 100px) 5vw 0", maxWidth: "800px", margin: "0 auto", scrollMarginTop: `${NAV_H + 20}px` }}>
      <Reveal>
        <Tag text="FAQ" />
        <h2 style={{ ...h2Style, marginBottom: "40px" }}>
          Common{" "}
          <span style={{ color: GOLD, fontStyle: "italic" }}>Questions</span>
        </h2>
      </Reveal>
      {FAQS.map((f, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <div style={{ borderBottom: `1px solid ${DARK_BORDER}` }}>
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)} style={{
              width: "100%", padding: "22px 0", background: "none", border: "none",
              cursor: "pointer", display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: "16px", textAlign: "left",
            }}>
              <span style={{
                fontFamily: "'Cormorant Garamond', serif", fontSize: "18px", fontWeight: 500,
                color: openIdx === i ? GOLD : TEXT_PRIMARY, transition: "color 0.3s ease",
              }}>{f.q}</span>
              <span style={{
                color: GOLD, fontSize: "18px", flexShrink: 0,
                transform: openIdx === i ? "rotate(45deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}>+</span>
            </button>
            <div style={{
              maxHeight: openIdx === i ? "300px" : "0", overflow: "hidden",
              transition: "max-height 0.5s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease",
              opacity: openIdx === i ? 1 : 0,
            }}>
              <p style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: "14px",
                color: TEXT_SECONDARY, lineHeight: 1.7, margin: "0 0 22px",
              }}>{f.a}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </section>
  );
}

// ─── HOME PAGE ───
function HomePage({ navigate }) {
  const scrollY = useScrollY();
  return (
    <div>
      {/* HERO */}
      <section style={{ minHeight: "100vh", position: "relative", display: "flex", alignItems: "center", overflow: "hidden", padding: "140px 5vw 100px" }}>
        <ParticleField />
        <div style={{ position: "absolute", top: "-20%", right: "-5%", width: "clamp(300px, 45vw, 650px)", height: "clamp(300px, 45vw, 650px)", borderRadius: "50%", background: `radial-gradient(circle, ${GOLD}08 0%, transparent 70%)`, transform: `translateY(${scrollY * 0.08}px)`, pointerEvents: "none" }} />
        
        {/* Orbital rings - hero only */}
        <Parallax speed={-0.08} style={{ position: "absolute", top: "12%", right: "6%", width: "clamp(220px, 28vw, 420px)", height: "clamp(220px, 28vw, 420px)", border: `1px solid ${GOLD}10`, borderRadius: "50%", pointerEvents: "none" }}>
          <div style={{ position: "absolute", inset: "35px", border: `1px solid ${GOLD}08`, borderRadius: "50%" }} />
          <div style={{ position: "absolute", inset: "70px", border: `1px solid ${GOLD}05`, borderRadius: "50%" }} />
          <div style={{ position: "absolute", top: 0, left: "50%", width: "6px", height: "6px", background: GOLD, borderRadius: "50%", boxShadow: `0 0 16px ${GOLD}50`, animation: "orbit 10s linear infinite", transformOrigin: "0 clamp(110px, 14vw, 210px)" }} />
        </Parallax>

        <div style={{ position: "relative", zIndex: 1, maxWidth: "720px" }}>
          <Reveal delay={0.05}>
            <div style={{ display: "inline-flex", alignItems: "flex-start", gap: "10px", marginBottom: "20px", paddingTop: "2px" }}>
              <span style={{ width: "28px", height: "1px", background: GOLD, marginTop: "9px", flexShrink: 0 }} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "14.5px", letterSpacing: "4px", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>Token Launch Strategy & Private OTC Capital</span>
            </div>
          </Reveal>

          <div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(40px, 7vw, 88px)", fontWeight: 500, lineHeight: 1.05, color: TEXT_PRIMARY, margin: "0 0 20px", letterSpacing: "-1px" }}>
              <Reveal delay={0.25} style={{ display: "block" }}>We build market-ready token launches</Reveal>
              <Reveal delay={0.5} style={{ display: "block" }}><span style={{ color: GOLD, fontStyle: "italic" }}>and facilitate</span> private OTC capital</Reveal>
            </h1>
          </div>

          <Reveal delay={0.35}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(15px, 1.5vw, 18px)", color: TEXT_HIGH, lineHeight: 1.75, margin: "0 0 12px", maxWidth: "540px" }}>
              We structure token launches, plan exchange strategy, and move private OTC allocations.
            </p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(13px, 1.2vw, 15px)", color: TEXT_SECONDARY, lineHeight: 1.7, margin: "0 0 44px", maxWidth: "540px" }}>
              Helping crypto founders and investors make better decisions around token launches and private markets.
            </p>
          </Reveal>

          <Reveal delay={0.5}>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <button onClick={() => navigate("Contact")} style={btnPrimary}>Let's Assess Your Structure<span style={{ marginLeft: "8px" }}>→</span></button>
              <button onClick={() => navigate("Services")} style={btnOutline}>Our Services</button>
            </div>
          </Reveal>
        </div>

        <div style={{ position: "absolute", bottom: "32px", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "9px", letterSpacing: "3px", textTransform: "uppercase", color: TEXT_DIM }}>Scroll</span>
          <div style={{ width: "1px", height: "40px", background: `linear-gradient(to bottom, ${GOLD}00, ${GOLD}50, ${GOLD}00)`, animation: "scrollPulse 2s ease-in-out infinite" }} />
        </div>
      </section>

      {/* STATS */}
      <section id="stats" style={{ borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`, background: DARK_DEEPER }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{ padding: "48px 24px", textAlign: "center", borderRight: i < STATS.length - 1 ? `1px solid ${DARK_BORDER}` : "none" }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 50px)", fontWeight: 300, color: GOLD, marginBottom: "6px" }}>
                  <AnimNum target={s.num} prefix={s.prefix} suffix={s.suffix} formatK={s.formatK} />
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "2px", textTransform: "uppercase", color: TEXT_DIM }}>{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <div style={{ textAlign: "center", padding: "0 0 16px" }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: TEXT_DIM, fontStyle: "italic" }}>Based on internal deal data and market observations</span>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section id="problem" style={{ padding: "clamp(80px, 10vw, 130px) 5vw", maxWidth: "1140px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="The Problem" />
          <h2 style={h2Style}>
            <StaggerText text="Most Token Launches Don't Fail Because of" />{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Marketing" delay={0.5} /></span>
          </h2>
          <p style={{ ...bodyStyle, maxWidth: "620px", marginBottom: "56px" }}>
            They fail because of poor liquidity structure, weak token utility, bad deal terms, and partners who aren't looking out for the project. The earlier you catch them, the better.
          </p>
        </Reveal>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
          {FAILURES.map((f, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <Parallax speed={0.02}>
                <div style={{ background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, padding: "40px 32px", position: "relative", overflow: "hidden" }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "52px", fontWeight: 300, color: GOLD, lineHeight: 1, marginBottom: "16px" }}>{f.stat}</div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: 0 }}>{f.text}</p>
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${GOLD}40, transparent)` }} />
                </div>
              </Parallax>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.35}>
          <div style={{ marginTop: "48px", padding: "32px 36px", borderLeft: `2px solid ${GOLD}`, background: `${GOLD}04` }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(20px, 2.5vw, 28px)", fontWeight: 500, fontStyle: "italic", color: TEXT_PRIMARY, lineHeight: 1.5, margin: 0 }}>
              That's where we come in. We sit alongside founders through the hard parts, protecting the decisions that make or break a launch.
            </p>
          </div>
        </Reveal>
      </section>

      {/* WHY CRYPTOBOOSTRA */}
      <section id="why" style={{ padding: "clamp(80px, 10vw, 130px) 5vw", borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`, background: DARK_DEEPER }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
          <Reveal>
            <Tag text="Why CryptoBoostra" />
            <h2 style={{ ...h2Style, marginBottom: "16px" }}>
              <StaggerText text="We Understand" />{" "}
              <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Market Structure" delay={0.2} /></span>
              {" "}<StaggerText text="Better Than Most" delay={0.35} />
            </h2>
            <p style={{ ...bodyStyle, maxWidth: "600px", marginBottom: "16px" }}>
              That's why founders and investors trust us with decisions that matter before and after a token launch.
            </p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_HIGH, lineHeight: 1.7, margin: "0 0 56px", maxWidth: "600px" }}>
              We've seen how deals are structured behind closed doors. That's why we know where they break.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
            {[
              { title: "Direct Relationships, No Unnecessary Chains", text: "We sit across the table from exchanges, market makers, and institutional buyers. Your costs stay low. Your terms stay fair." },
              { title: "Liquidity-First Thinking", text: "We evaluate every deal on order book depth and exit liquidity, not token price or inflated market caps and volumes. If the liquidity isn't real, the opportunity isn't either." },
              { title: "Founder Protection, Always", text: "Your treasury, your terms. We structure deals that keep founders in control of their unlock schedules, listing costs, and market maker arrangements." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <CardHover>
                  <DrawLine />
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "22px", fontWeight: 500, color: TEXT_PRIMARY, margin: "0 0 14px" }}>{item.title}</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: 0 }}>{item.text}</p>
                </CardHover>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section id="outcomes" style={{ padding: "clamp(80px, 10vw, 130px) 5vw", maxWidth: "1140px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="After Working With Us" />
          <h2 style={{ ...h2Style, marginBottom: "48px" }}>
            <StaggerText text="What Actually" />{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Changes" delay={0.2} /></span>
          </h2>
        </Reveal>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {OUTCOMES.map((item, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div style={{ display: "flex", alignItems: "center", gap: "20px", padding: "22px 0", borderBottom: `1px solid ${DARK_BORDER}` }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: GOLD, flexShrink: 0, boxShadow: `0 0 12px ${GOLD}30` }} />
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: TEXT_HIGH, lineHeight: 1.6, margin: 0 }}>{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.4}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "13px", color: TEXT_SECONDARY, marginTop: "28px", lineHeight: 1.6 }}>
            Most founders don't see the weak points until they're already in the market. We help you find them earlier.
          </p>
        </Reveal>
      </section>

      {/* SERVICES PREVIEW */}
      <section id="services-preview" style={{ padding: "clamp(80px, 10vw, 130px) 5vw", borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`, background: DARK_DEEPER }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
          <Reveal>
            <Tag text="Services" />
            <h2 style={{ ...h2Style, marginBottom: "48px" }}>
              <StaggerText text="Everything You Need to" />{" "}
              <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Build and Launch" delay={0.3} /></span>
            </h2>
          </Reveal>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {SERVICES.map((s, i) => <ServiceRow key={i} service={s} index={i} />)}
          </div>
          <Reveal delay={0.3}>
            <div style={{ marginTop: "48px", textAlign: "center" }}>
              <button onClick={() => navigate("Services")} style={btnPrimary}>View Full Services →</button>
            </div>
          </Reveal>
        </div>
      </section>

      <TestimonialCarousel />
      <CTABlock navigate={navigate} />
    </div>
  );
}

// ─── ABOUT PAGE ───
function AboutPage({ navigate }) {
  return (
    <div style={{ paddingTop: "140px" }}>
      <section style={{ padding: "0 5vw 100px", maxWidth: "860px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="Our Story" />
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px, 5.5vw, 64px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 48px", lineHeight: 1.1 }}>
            <StaggerText text="Built for Better" />{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Decisions" delay={0.25} /></span>
            {" "}<StaggerText text="From Day One" delay={0.35} />
          </h1>
        </Reveal>

        <Reveal delay={0.1}><DrawLine width="48px" /></Reveal>

        {[
          { text: "Founded in 2021, CryptoBoostra started with a simple observation: too many ambitious crypto teams were getting the structure wrong . Not because they lacked ambition, but because the decisions around their token, liquidity, exchanges, market makers, and capital could have lasting consequences.", lead: true },
          { text: "Exchange deals that don't make sense for where the project is. Market makers with terms that deserve closer attention. Unlock schedules that create unnecessary pressure post-TGE. Capital partners who don't follow through. We've seen how these things play out, and we built CryptoBoostra to help founders and investors make better decisions before they become expensive." },
          { text: "We structure token launches, plan exchange listings, and move private OTC allocations. Our network includes 90+ exchanges, market makers, and investors across the market." },
          { text: "We've worked with L1s, L2s, and early-stage tokens preparing for Tier-1 listings. Over 50 projects advised, $1M+ in deals closed. We've seen how deals are structured behind closed doors. That's why we know where they break." },
        ].map((item, i) => (
          <Reveal key={i} delay={0.12 + i * 0.06}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: item.lead ? "clamp(16px, 1.5vw, 19px)" : "15px", color: item.lead ? TEXT_HIGH : TEXT_SECONDARY, lineHeight: 1.8, margin: "0 0 26px" }}>{item.text}</p>
          </Reveal>
        ))}

        <Reveal delay={0.5}>
          <div style={{ marginTop: "40px", padding: "32px 36px", borderLeft: `2px solid ${GOLD}`, background: `${GOLD}04` }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(20px, 2.5vw, 28px)", fontWeight: 500, fontStyle: "italic", color: TEXT_PRIMARY, lineHeight: 1.5, margin: 0 }}>
              "We prioritize trust and transparency in every deal. That's not marketing. It's how we decide what's worth our time."
            </p>
          </div>
        </Reveal>

        {/* TEAM */}
        <Reveal delay={0.2}>
          <div id="team" style={{ marginTop: "80px", scrollMarginTop: `${NAV_H + 20}px` }}>
            <Tag text="The Team" />
            <h2 style={{ ...h2Style, marginBottom: "36px" }}>Leadership</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
              {TEAM.map((m, i) => (
                <div key={i} style={{ background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, padding: "36px 24px", textAlign: "center" }}>
                  <div style={{
                    width: "72px", height: "72px", borderRadius: "50%",
                    background: DARK_DEEPER, border: `1px solid ${DARK_BORDER}`,
                    margin: "0 auto 18px", display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "'Cormorant Garamond', serif", fontSize: "24px", fontWeight: 300, color: GOLD + "40",
                  }}>{m.name[0]}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500, color: TEXT_PRIMARY, marginBottom: "4px" }}>{m.name}</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM, marginBottom: "14px" }}>{m.role}</div>
                  {m.links.length > 0 && (
                    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                      {m.links.map((link, j) => (
                        <a key={j} href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: TEXT_SECONDARY, transition: "color 0.3s ease", display: "flex", alignItems: "center" }}>
                          {link.type === "linkedin" && <LinkedInIcon size={16} />}
                          {link.type === "telegram" && <TelegramIcon size={16} />}
                          {link.type === "email" && <EmailIcon size={16} />}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
      <CTABlock navigate={navigate} />
    </div>
  );
}

// ─── SERVICES PAGE ───
function ServicesPage({ navigate }) {
  return (
    <div style={{ paddingTop: "140px" }}>
      <section style={{ padding: "0 5vw 80px", maxWidth: "1140px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="Services" />
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px, 5.5vw, 64px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 16px", lineHeight: 1.1 }}>
            <StaggerText text="Everything You Need to" />{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Build and Launch" delay={0.3} /></span>
          </h1>
          <p style={{ ...bodyStyle, maxWidth: "580px" }}>Every service is backed by real relationships, hands-on deal experience, and a commitment to execution.</p>
        </Reveal>
      </section>

      <section style={{ padding: "0 5vw 100px", maxWidth: "1140px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" }}>
          {SERVICES.map((s, i) => <Reveal key={i} delay={i * 0.08}><ServiceCard service={s} /></Reveal>)}
        </div>
      </section>

      <section id="process" style={{ padding: "clamp(80px, 10vw, 120px) 5vw", borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`, background: DARK_DEEPER, scrollMarginTop: `${NAV_H + 20}px` }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <Reveal>
            <Tag text="Process" />
            <h2 style={{ ...h2Style, marginBottom: "48px" }}>
              <StaggerText text="How We" />{" "}
              <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Work" delay={0.15} /></span>
            </h2>
          </Reveal>
          {[
            { n: "01", title: "Discovery", desc: "We learn your project, goals, timeline, and what you have tried before. This is where we listen." },
            { n: "02", title: "Structure and Strategy", desc: "We map the strategy: token structure, exchanges, deal terms, timelines. and everything that needs to be stress-tested before execution." },
            { n: "03", title: "Execution", desc: "We make introductions, negotiate terms, and stay in the room with you through execution." },
            { n: "04", title: "Ongoing Support", desc: "Post-deal, we stay involved. Market making oversight, investor relations, and private OTC opportunities." },
          ].map((item, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{ display: "grid", gridTemplateColumns: "50px 1fr", gap: "24px", padding: "28px 0", borderBottom: `1px solid ${DARK_BORDER}` }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 300, color: GOLD + "25" }}>{item.n}</div>
                <div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500, color: TEXT_PRIMARY, margin: "0 0 8px" }}>{item.title}</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABlock navigate={navigate} />
    </div>
  );
}

// ─── CONTACT PAGE ───
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const upd = (f) => (e) => { setForm({ ...form, [f]: e.target.value }); setErrors({ ...errors, [f]: false }); };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = true;
    if (!form.message.trim()) newErrors.message = true;
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setSending(true);
    try {
      await fetch("https://api.web3forms.com/submit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_key: "45826188-3a4e-43c1-832c-b2c9c16f0908", ...form, from_name: "CryptoBoostra Website" })
      });
      setSent(true);
    } catch (e) { setSent(true); }
    setSending(false);
  };

  return (
    <div style={{ paddingTop: "140px" }}>
      <section style={{ padding: "0 5vw 120px", maxWidth: "1140px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "72px" }}>
          <div>
            <Reveal>
              <Tag text="Contact" />
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 16px", lineHeight: 1.1 }}>
                <StaggerText text="Let's Talk" />{" "}
                <span style={{ color: GOLD, fontStyle: "italic" }}><StaggerText text="Deals" delay={0.2} /></span>
              </h1>
              <p style={{ ...bodyStyle, marginBottom: "44px" }}>Whether you are planning a token launch, need exchange introductions, or want to explore OTC opportunities, we are here to talk.</p>
            </Reveal>

            <Reveal delay={0.15}>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {[
                  { label: "Email", value: "hello@cryptoboostra.com", href: "mailto:hello@cryptoboostra.com" },
                  { label: "Response Time", value: "Within 24 hours" },
                  { label: "Global Presence", value: "Africa · Dubai · Singapore" },
                ].map((item, i) => (
                  <div key={i}>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: GOLD, marginBottom: "4px" }}>{item.label}</div>
                    {item.href ? (
                      <a href={item.href} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: TEXT_PRIMARY, textDecoration: "none" }}>{item.value}</a>
                    ) : (
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: TEXT_PRIMARY }}>{item.value}</div>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div style={{ marginTop: "36px" }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: TEXT_DIM, marginBottom: "12px" }}>Connect</div>
                <div style={{ display: "flex", gap: "10px" }}>
                  {[
                    { icon: <LinkedInIcon />, label: "LinkedIn", url: "https://www.linkedin.com/company/cryptoboostra/" },
                    { icon: <TelegramIcon />, label: "Telegram", url: "https://t.me/Cryptoboostra" },
                  ].map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.label} style={{
                      width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center",
                      background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, color: TEXT_SECONDARY, textDecoration: "none", transition: "all 0.3s ease",
                    }}>{s.icon}</a>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div style={{ marginTop: "36px" }}>
                <a href="https://calendly.com/meritnkwocha" target="_blank" rel="noopener noreferrer" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none", textAlign: "center" }}>Book a Call →</a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            {sent ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "480px", background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, textAlign: "center", padding: "48px" }}>
                <div>
                  <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: `${GOLD}12`, border: `2px solid ${GOLD}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "22px", color: GOLD }}>✓</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "26px", fontWeight: 300, color: TEXT_PRIMARY, margin: "0 0 10px" }}>Message Sent</h3>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, margin: 0 }}>We'll get back to you within 24 hours.</p>
                </div>
              </div>
            ) : (
              <div style={{ background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, padding: "44px 36px" }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400, color: TEXT_PRIMARY, marginBottom: "28px" }}>Send Us a Message</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
                  {[
                    { f: "name", label: "Full Name", type: "text", ph: "Your name", required: true },
                    { f: "email", label: "Email", type: "email", ph: "you@company.com", required: true },
                    { f: "phone", label: "Phone (Optional)", type: "tel", ph: "+1 (555) 000-0000", required: false },
                  ].map(({ f, label, type, ph, required }) => (
                    <div key={f}>
                      <label style={labelSt}>{label}</label>
                      <input type={type} value={form[f]} onChange={upd(f)} placeholder={ph} style={{ ...inputSt, borderColor: errors[f] ? GOLD : DARK_BORDER }}
                        onFocus={e => e.target.style.borderColor = GOLD + "40"}
                        onBlur={e => { if (!errors[f]) e.target.style.borderColor = DARK_BORDER; }} />
                      {errors[f] && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: GOLD, marginTop: "4px", display: "block" }}>{f === "email" ? "Please enter a valid email" : "This field is required"}</span>}
                    </div>
                  ))}
                  <div>
                    <label style={labelSt}>Your Message</label>
                    <textarea value={form.message} onChange={upd("message")} rows={5}
                      placeholder="Tell us about your project and what you need help with..."
                      style={{ ...inputSt, resize: "vertical", minHeight: "110px", borderColor: errors.message ? GOLD : DARK_BORDER }}
                      onFocus={e => e.target.style.borderColor = GOLD + "40"}
                      onBlur={e => { if (!errors.message) e.target.style.borderColor = DARK_BORDER; }} />
                    {errors.message && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: GOLD, marginTop: "4px", display: "block" }}>This field is required</span>}
                  </div>
                  <button onClick={handleSubmit} disabled={sending} style={{ ...btnPrimary, width: "100%", opacity: sending ? 0.6 : 1 }}>{sending ? "Sending..." : "Talk to Us →"}</button>
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ─── PRIVACY POLICY ───
function PrivacyPage() {
  return (
    <div style={{ paddingTop: "140px" }}>
      <section style={{ padding: "0 5vw 100px", maxWidth: "800px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="Legal" />
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 400, color: TEXT_PRIMARY, margin: "0 0 40px", lineHeight: 1.1 }}>Privacy Policy</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM, marginBottom: "32px" }}>Last updated: March 2026</p>
        </Reveal>
        {[
          { title: "Overview", text: "CryptoBoostra values your privacy. This policy explains what information we collect, how we use it, and your rights regarding your data. We are committed to protecting the personal information of everyone who interacts with our website and services." },
          { title: "Information We Collect", text: "When you use our contact form, we collect your name, email address, phone number (if provided), and the content of your message. We also collect basic usage data such as pages visited and time spent on the site through standard analytics. We do not collect financial information, wallet addresses, or any sensitive personal data through our website." },
          { title: "How We Use Your Information", text: "We use the information you provide through our contact form solely to respond to your inquiry and to communicate with you about our services. We may use anonymized usage data to improve our website experience. We do not use your information for automated decision-making or profiling." },
          { title: "Data Sharing", text: "We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, but only to the extent necessary for them to provide these services to us. These parties are obligated to keep your information confidential." },
          { title: "Data Retention", text: "We retain your personal information only for as long as necessary to fulfill the purpose for which it was collected, or as required by applicable laws. Contact form submissions are retained for the duration of any active business relationship and for a reasonable period thereafter." },
          { title: "Your Rights", text: "You have the right to request access to the personal data we hold about you, to request correction of any inaccurate data, and to request deletion of your data. To exercise any of these rights, please contact us at hello@cryptoboostra.com." },
          { title: "Cookies", text: "Our website may use essential cookies to ensure basic functionality. We do not use tracking cookies or third-party advertising cookies." },
          { title: "Changes to This Policy", text: "We may update this privacy policy from time to time. Any changes will be reflected on this page with an updated revision date." },
          { title: "Contact", text: "If you have any questions about this privacy policy, please contact us at hello@cryptoboostra.com." },
        ].map((s, i) => (
          <Reveal key={i} delay={0.05 + i * 0.03}>
            <div style={{ marginBottom: "28px" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500, color: TEXT_PRIMARY, margin: "0 0 10px" }}>{s.title}</h3>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: 0 }}>{s.text}</p>
            </div>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

// ─── TERMS OF SERVICE ───
function TermsPage() {
  return (
    <div style={{ paddingTop: "140px" }}>
      <section style={{ padding: "0 5vw 100px", maxWidth: "800px", margin: "0 auto" }}>
        <Reveal>
          <Tag text="Legal" />
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 400, color: TEXT_PRIMARY, margin: "0 0 40px", lineHeight: 1.1 }}>Terms of Service</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM, marginBottom: "32px" }}>Last updated: March 2026</p>
        </Reveal>
        {[
          { title: "Agreement", text: "By accessing and using the CryptoBoostra website, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our website." },
          { title: "Services", text: "CryptoBoostra provides advisory and business development services for crypto projects and investors. Our services include token launch structuring, exchange and market maker strategy, private OTC capital and deal structuring. The specific scope of services is defined in individual client agreements." },
          { title: "Advisory Nature", text: "All information and services provided by CryptoBoostra are advisory in nature. We do not provide financial advice, investment advice, or legal advice. Our services are based on our experience and industry knowledge, and outcomes may vary. Clients are responsible for their own decisions and should consult with qualified financial, legal, and tax professionals before making any investment or business decisions." },
          { title: "No Guarantees", text: "CryptoBoostra does not guarantee any specific outcomes, returns, or results from our advisory services. The cryptocurrency market is volatile and unpredictable. Past performance of projects we have worked with does not guarantee future results." },
          { title: "Confidentiality", text: "We treat all client information and deal details as confidential. We do not disclose client relationships, deal terms, or project details without explicit written consent. This obligation survives the termination of any client relationship." },
          { title: "Intellectual Property", text: "All content on this website, including text, design, graphics, and logos, is the property of CryptoBoostra and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from our content without prior written permission." },
          { title: "Limitation of Liability", text: "CryptoBoostra shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our website or services. Our total liability for any claim arising from our services shall not exceed the fees paid by the client for the specific engagement giving rise to the claim." },
          { title: "Governing Law", text: "These terms shall be governed by and construed in accordance with applicable laws. Any disputes arising from these terms or our services shall be resolved through good-faith negotiation before pursuing any other remedies." },
          { title: "Changes", text: "We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to this page. Continued use of our website after changes constitutes acceptance of the updated terms." },
          { title: "Contact", text: "For questions about these terms, please contact us at hello@cryptoboostra.com." },
        ].map((s, i) => (
          <Reveal key={i} delay={0.05 + i * 0.03}>
            <div style={{ marginBottom: "28px" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500, color: TEXT_PRIMARY, margin: "0 0 10px" }}>{s.title}</h3>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: 0 }}>{s.text}</p>
            </div>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

// ─── STYLES ───
const h2Style = { fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 600, color: TEXT_PRIMARY, margin: "0 0 20px", lineHeight: 1.15 };
const bodyStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: "15px", color: TEXT_SECONDARY, lineHeight: 1.7, margin: "0 0 16px" };
const btnPrimary = { fontFamily: "'DM Sans', sans-serif", fontSize: "12px", fontWeight: 500, letterSpacing: "1.5px", textTransform: "uppercase", padding: "15px 32px", background: GOLD, color: DARK, border: "none", cursor: "pointer", transition: "all 0.3s ease" };
const btnOutline = { fontFamily: "'DM Sans', sans-serif", fontSize: "12px", fontWeight: 500, letterSpacing: "1.5px", textTransform: "uppercase", padding: "15px 32px", background: "transparent", color: GOLD, border: `1px solid ${GOLD}25`, cursor: "pointer", transition: "all 0.3s ease" };
const inputSt = { width: "100%", padding: "13px 16px", background: DARK, border: `1px solid ${DARK_BORDER}`, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", fontSize: "14px", outline: "none", transition: "border-color 0.3s ease", boxSizing: "border-box" };
const labelSt = { fontFamily: "'DM Sans', sans-serif", fontSize: "10px", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: TEXT_DIM, display: "block", marginBottom: "8px" };

// ─── MAIN APP ───
export default function CryptoBoostra() {
  const [page, setPage] = useState("Home");
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const navigate = (p) => { setPage(p); setMobileMenu(false); window.scrollTo({ top: 0, behavior: "instant" }); };

  const scrollToSection = (pageTarget, sectionId) => {
    const go = () => {
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) { const top = el.getBoundingClientRect().top + window.scrollY - NAV_H - 20; window.scrollTo({ top, behavior: "instant" }); }
      }, 150);
    };
    if (page !== pageTarget) { setPage(pageTarget); setMobileMenu(false); go(); } else { go(); }
  };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <div style={{ background: DARK, color: TEXT_PRIMARY, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        ::selection { background: ${GOLD}35; color: ${TEXT_PRIMARY}; }
        button:hover { opacity: 0.88; }
        a:hover { opacity: 0.8; }
        @keyframes orbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes scrollPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes staggerIn { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .stagger-line1 { display: inline; opacity: 0; animation: staggerIn 0.7s cubic-bezier(0.16,1,0.3,1) 0.4s forwards; }
        .stagger-line2 { display: inline; opacity: 0; animation: staggerIn 0.7s cubic-bezier(0.16,1,0.3,1) 0.65s forwards; }
        .hero-line1 { display: inline; opacity: 0; animation: staggerIn 0.7s cubic-bezier(0.16,1,0.3,1) 0.5s forwards; }
        .hero-line2 { display: inline; opacity: 0; animation: staggerIn 0.7s cubic-bezier(0.16,1,0.3,1) 0.8s forwards; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${DARK}; }
        ::-webkit-scrollbar-thumb { background: ${GOLD}25; }
        input::placeholder, textarea::placeholder { color: ${TEXT_DIM}; }
        @media (max-width: 768px) { .desk-nav { display: none !important; } .mob-btn { display: flex !important; } }
        @media (min-width: 769px) { .desk-nav { display: flex !important; } .mob-btn { display: none !important; } .mob-menu { display: none !important; } }
      `}</style>

      <Grain />
      <ScrollProgress />

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        padding: scrolled ? "14px 5vw" : "22px 5vw",
        background: scrolled ? DARK + "F0" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${DARK_BORDER}` : "1px solid transparent",
        transition: "all 0.4s ease", display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div onClick={() => navigate("Home")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}>
          <Logo size={36} />
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" }}><span style={{ color: TEXT_PRIMARY }}>Crypto</span><span style={{ color: GOLD }}>Boostra</span></span>
        </div>

        <div className="desk-nav" style={{ display: "flex", alignItems: "center", gap: "32px" }}>
          <div style={{ display: "flex", gap: "28px" }}>
            {PAGES.map(p => (
              <button key={p} onClick={() => p === "Testimonials" ? scrollToSection("Home", "testimonials") : navigate(p)} style={{
                background: "none", border: "none", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: 500,
                letterSpacing: "2px", textTransform: "uppercase", color: page === p ? GOLD : TEXT_SECONDARY,
                cursor: "pointer", padding: "4px 0", borderBottom: page === p ? `1px solid ${GOLD}` : "1px solid transparent", transition: "all 0.3s ease",
              }}>{p}</button>
            ))}
          </div>
          <button onClick={() => navigate("Contact")} style={{ ...btnPrimary, padding: "10px 22px", fontSize: "10px" }}>Contact Us</button>
        </div>

        <button className="mob-btn" onClick={() => setMobileMenu(!mobileMenu)} style={{
          display: "none", flexDirection: "column", gap: "5px", background: "none", border: "none", cursor: "pointer", padding: "8px",
        }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: "20px", height: "1.5px", background: TEXT_PRIMARY, transition: "all 0.3s ease",
              transform: mobileMenu ? i === 0 ? "rotate(45deg) translate(4.5px, 4.5px)" : i === 2 ? "rotate(-45deg) translate(4.5px, -4.5px)" : "scaleX(0)" : "none",
            }} />
          ))}
        </button>
      </nav>

      {mobileMenu && (
        <div className="mob-menu" style={{
          position: "fixed", top: "56px", left: 0, right: 0, bottom: 0,
          background: DARK + "FA", backdropFilter: "blur(20px)", zIndex: 999,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "28px",
        }}>
          {["Home", "About", "Services"].map(p => (
            <button key={p} onClick={() => navigate(p)} style={{
              background: "none", border: "none", fontFamily: "'Cormorant Garamond', serif", fontSize: "28px",
              fontWeight: 300, letterSpacing: "3px", textTransform: "uppercase", color: page === p ? GOLD : TEXT_PRIMARY, cursor: "pointer",
            }}>{p}</button>
          ))}
          <button onClick={() => { scrollToSection("Home", "testimonials"); }} style={{
            background: "none", border: "none", fontFamily: "'Cormorant Garamond', serif", fontSize: "28px",
            fontWeight: 300, letterSpacing: "3px", textTransform: "uppercase", color: TEXT_PRIMARY, cursor: "pointer",
          }}>Testimonials</button>
          <button onClick={() => navigate("Contact")} style={{ ...btnPrimary, marginTop: "12px" }}>Contact Us →</button>
        </div>
      )}

      <main>
        <PageTransition pageKey={page}>
          {page === "Home" && <HomePage navigate={navigate} />}
          {page === "About" && <AboutPage navigate={navigate} />}
          {page === "Services" && <ServicesPage navigate={navigate} />}
          {page === "Contact" && <ContactPage />}
          {page === "Privacy" && <PrivacyPage />}
          {page === "Terms" && <TermsPage />}
        </PageTransition>
      </main>

      {/* FAQ */}
      <FAQSection />

      {/* FOOTER */}
      <footer style={{ borderTop: `1px solid ${DARK_BORDER}`, padding: "48px 5vw", background: DARK_DEEPER }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "36px" }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "16px", fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase", color: GOLD + "80", marginBottom: "12px" }}>CryptoBoostra</div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: TEXT_DIM, lineHeight: 1.6, marginBottom: "8px" }}>Protecting founders and investors since 2021.</p>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_DIM + "80", lineHeight: 1.6 }}>Trust. Transparency. Integrity.</p>
          </div>
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: TEXT_DIM, marginBottom: "14px" }}>Explore</div>
            {[
              { label: "Home", action: () => navigate("Home") },
              { label: "About", action: () => navigate("About") },
              { label: "Services", action: () => navigate("Services") },
              { label: "Team", action: () => scrollToSection("About", "team") },
              { label: "Testimonials", action: () => scrollToSection("Home", "testimonials") },
              { label: "FAQ", action: () => scrollToSection(page, "faq") },
              { label: "Contact", action: () => navigate("Contact") },
            ].map((item, i) => (
              <button key={i} onClick={item.action} style={{
                display: "block", background: "none", border: "none", fontFamily: "'DM Sans', sans-serif",
                fontSize: "13px", color: TEXT_SECONDARY, cursor: "pointer", padding: "3px 0", marginBottom: "4px", textAlign: "left",
              }}>{item.label}</button>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", fontWeight: 500, letterSpacing: "3px", textTransform: "uppercase", color: TEXT_DIM, marginBottom: "14px" }}>Connect</div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              {[
                { icon: <LinkedInIcon />, label: "LinkedIn", url: "https://www.linkedin.com/company/cryptoboostra/" },
                { icon: <TelegramIcon />, label: "Telegram", url: "https://t.me/Cryptoboostra" },
              ].map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.label} style={{
                  width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center",
                  background: DARK_CARD, border: `1px solid ${DARK_BORDER}`, color: TEXT_SECONDARY, textDecoration: "none", transition: "all 0.3s ease",
                }}>{s.icon}</a>
              ))}
            </div>
          </div>
        </div>

        <div style={{ maxWidth: "1140px", margin: "32px auto 0", paddingTop: "20px", borderTop: `1px solid ${DARK_BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_DIM }}>© 2026 CryptoBoostra. All rights reserved.</span>
          <div style={{ display: "flex", gap: "20px" }}>
            <button onClick={() => navigate("Privacy")} style={{ background: "none", border: "none", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_DIM, cursor: "pointer" }}>Privacy Policy</button>
            <button onClick={() => navigate("Terms")} style={{ background: "none", border: "none", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: TEXT_DIM, cursor: "pointer" }}>Terms of Service</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

